import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ProductsService } from '../products.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';
import { VendorService } from '../../vendors/vendor.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ManufacturerService } from '../../manufacturers/manufacturers.service';
import {
  NgOptionTemplateDirective,
  NgSelectComponent,
} from '@ng-select/ng-select';
import { VariantBarcodesPanelComponent } from '../variant-barcodes-panel/variant-barcodes-panel.component';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  of,
  Subject,
  switchMap,
  tap,
  Subscription,
  Observable,
  firstValueFrom,
} from 'rxjs';
import { NavigationStart } from '@angular/router';
import { ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';
import { HsnService, HsnValidationResult } from '../../hsn/hsn.service';
import {
  ConfigurationService,
  SETTING_KEYS,
} from '../../configuration/configuration.service';

/**
 * HSN must be 4, 6 or 8 digits.
 *
 * The previous /^\d{6,8}$/ pattern was wrong in both directions: it accepted 7
 * digits, which is never a valid HSN (almost always a leading zero eaten by a
 * spreadsheet), and rejected 4, which is valid for turnover at or below Rs 5
 * crore under Notification 78/2020.
 */
export function hsnLengthValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = String(control.value ?? '').trim();
    if (!raw) return null; // `required` owns emptiness
    if (!/^\d+$/.test(raw)) return { hsnNotNumeric: true };
    const len = raw.length;
    if ([4, 6, 8].includes(len)) return null;
    if ([3, 5, 7].includes(len)) return { hsnMissingDigit: { length: len } };
    return { hsnLength: { length: len } };
  };
}

@Component({
  selector: 'app-product-modal',
  templateUrl: './add-product.component.html',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    NgSelectComponent,
    NgOptionTemplateDirective,
    VariantBarcodesPanelComponent,
  ],
  styleUrls: ['./add-product.component.scss'],
})
export class AddProductComponent implements OnInit {
  private fb = inject(FormBuilder);
  private productService = inject(ProductsService);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private route = inject(ActivatedRoute);
  private vendorService = inject(VendorService);
  private manufacturerService = inject(ManufacturerService);
  private configurationService = inject(ConfigurationService);

  /**
   * Configuration > Products: which of vendor / manufacturer / brand must be
   * filled. Defaults mirror the server's (vendor on, the others off) until
   * the settings call answers; the server enforces the real rule on save.
   */
  masterRequired = { vendor: true, manufacturer: false, brand: false };
  /** products.brandLinkedToManufacturer: brand list follows the manufacturer. */
  brandLinked = false;
  /** The manufacturer was filled in from the picked brand, not chosen by hand. */
  private mfrFromBrand = false;
  private confirmDialog = inject(ConfirmDialogService);
  vendors$!: Observable<any[]>;

  navigationSubscription!: Subscription;

  productForm: FormGroup;
  isEdit = false;
  productId: any;
  vendors: any = [];
  categories: any = [];
  manufacturers: any = [];
  departments: any = [];
  manufacturersBackup: any = [];
  brandOptions: any = [];
  // Every brand of the tenant with its manufacturer (null for a standalone
  // brand). The brand list, narrowed to the picked manufacturer only when
  // brandLinked is on.
  allBrands: any[] = [];
  // Saved price rows by id, for the read-only "last updated" / retired info.
  pricingMeta = new Map<string, { updatedAt?: string; isActive?: boolean }>();
  subCategories: any = [];
  units = ['PCS', 'Kgs', 'Grams'];
  gstSlabs: any = [];
  showPricingIndex: number = -1;
  currentStep = 1;
  title: string = '';
  isFormInitializing = false;
  isBulk: Boolean = false;
  packSizeGm: number = 0;
  bulkParentProductId: string = '';
  parentProductSearch$ = new Subject<string>();
  isLoadingParentProducts = false;
  parentProducts: any[] = [];
  searchProductName: any = [];
  parentProductNameSearch$ = new Subject<string>();
  isLoadingParentProductsByName = false;
  parentProductsByName: any[] = [];
  formSubmitted: Boolean = false;
  showBulkProductsDetails: boolean = false;

  constructor(private cdr: ChangeDetectorRef) {
    this.productForm = this.fb.group({
      packSizeGm: [null],
      bulkParentProductId: [null],
      searchProductName: [''],
      //name: ['', Validators.required],
      shortDescription: ['', Validators.required],
      longDescription: ['', Validators.required],
      manufacturerId: [null],
      departmentId: [null, Validators.required],
      brandId: [null],
      categoryId: [null, Validators.required],
      subCategoryId: [null],
      vendorsId: [null, Validators.required],
      uom: [null, Validators.required],
      hsnCode: ['', [Validators.required, hsnLengthValidator()]],
      purchaseGstRateId: [null, Validators.required],
      // saleGstRateId:['',Validators.required],
      sku: ['', Validators.required],
      photoUrl: [''],
      innerPackaging: [''],
      outerPackaging: [''],
      productSpecificPaymentTerms: [0],
      requiresBatchExpiryTracking: [false],
      marginPercentage: [0],
      isActive: [true],
      flowThrough: [false],
      returnToVendor: [true],
      isConsumable: [false],
      isPerishable: [false],
      isTemperatureControlled: [false],
      shelfLifeDays: [''],
      minShelfLifeDays: [''],
      minShelfLifePercent: [''],
      serialNumberControlled: [false],
      variants: this.fb.array([]),
      cessApplicable: [false],
      cessRate: [],
      isBulk: [false],
      isWeighable: [false],
      isDSD: [false],
      isKit: [false],
    });
  }

  /** Result of the last live HSN check. Null while idle or in flight. */
  hsnCheck: HsnValidationResult | null = null;
  hsnChecking = false;
  private readonly hsnService = inject(HsnService);
  private readonly hsnCheck$ = new Subject<string>();

  /**
   * Ask the server whether the typed HSN actually exists.
   *
   * The form validator only checks shape — HSN codes carry no checksum, so
   * "12345678" is structurally perfect and completely invalid. Only the master
   * lookup can catch that, and catching it here means the user finds out while
   * typing rather than via a 400 on save.
   *
   * Debounced, and last-write-wins via switchMap so a slow response for an
   * earlier keystroke cannot overwrite a newer result.
   */
  private initHsnLiveCheck(): void {
    this.hsnCheck$
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap(() => {
          this.hsnCheck = null;
        }),
        filter((code) => /^\d{4,8}$/.test(code)),
        tap(() => {
          this.hsnChecking = true;
        }),
        switchMap((code) =>
          this.hsnService.validate(code, { expectKind: 'GOODS' }).pipe(
            catchError(() => of(null)),
            finalize(() => {
              this.hsnChecking = false;
              this.cdr.markForCheck();
            }),
          ),
        ),
      )
      .subscribe((result) => {
        this.hsnCheck = result;
        this.cdr.markForCheck();
      });

    this.productForm
      .get('hsnCode')
      ?.valueChanges.subscribe((v: string) => this.hsnCheck$.next((v ?? '').trim()));
  }

  ngOnInit(): void {
    this.loadMasterRequirements();
    this.initHsnLiveCheck();
    const currentUrl = this.router.url;

    if (currentUrl.includes('/edit-product')) {
      this.title = 'Edit Product';
      this.isEdit = true;
    } else if (currentUrl.includes('/add-product')) {
      this.title = 'Add Product';
      this.isEdit = false;
      console.log(`is edit : ${this.isEdit}`);
    }

    this.navigationSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (!event.url.includes('/add-product')) {
          localStorage.removeItem('productFormData');
        }
      }
    });

    // 💾 Restore form data if available
    const savedForm = localStorage.getItem('productFormData');
    this.productForm.valueChanges.subscribe((formValue) => {
      localStorage.setItem('productFormData', JSON.stringify(formValue));
    });

    this.productId = this.route.snapshot.paramMap.get('id');
    this.isEdit = !!this.productId;

    // FIXED: Load basic data first, then handle edit/saved form logic
    this.loadBasicData().then(() => {
      if (this.isEdit) {
        this.loadProductForEdit();
      } else if (savedForm) {
        this.loadSavedForm(savedForm);
      } else {
        this.addVariant();
      }
    });

    this.setupFormSubscriptions();
    this.setupSearchSubscriptions();
    this.getAllManufacturers();
    this.loadAllBrands();
  }

  // ADDED: Separate method to load basic data with proper sequencing
  private async loadBasicData(): Promise<void> {
    return new Promise((resolve) => {
      let completedRequests = 0;
      const totalRequests = 3;

      const checkCompletion = () => {
        completedRequests++;
        if (completedRequests === totalRequests) {
          resolve();
        }
      };

      this.getAllDepartments().finally(() => checkCompletion());
      this.getAllVendors().finally(() => checkCompletion());
      this.loadGstSlabs().finally(() => checkCompletion());
    });
  }

  // ADDED: Separate method to handle product loading for edit mode
  private loadProductForEdit(): void {
    this.isFormInitializing = true;
    this.productService.getProductById(this.productId).subscribe((res: any) => {
      // FIXED: Load categories first if departmentId exists

      this.productForm.get('isBulk')?.setValue(res.isBulk); // Ensure the form control is also set
      this.isBulk = res.isBulk; // Set the UI-controlling boolean first
      this.productForm.get('isWeighable')?.setValue(!!res.isWeighable);

      if (res.isBulk && res.bulkParentProductId) {
        this.productService
          .getProductById(res.bulkParentProductId)
          .subscribe((parentProduct: any) => {
            this.parentProducts = [parentProduct];
            // Set the value after it has been loaded
            this.productForm
              .get('bulkParentProductId')
              ?.setValue(parentProduct.id);
            this.cdr.detectChanges(); // Force change detection
          });
      }
      if (res.departmentId) {
        this.onDepartmentChange(res.departmentId).then(() => {
          // FIXED: Load subcategories after categories are loaded
          if (res.categoryId) {
            this.getSubCategories(res.categoryId).then(() => {
              this.patchProductData(res);
            });
          } else {
            this.patchProductData(res);
          }
        });
      } else {
        this.patchProductData(res);
      }
    });
  }

  // ADDED: Separate method to handle saved form loading
  private loadSavedForm(savedForm: string): void {
    this.isFormInitializing = true;
    const parsed = JSON.parse(savedForm);

    // FIXED: Load categories and subcategories in proper sequence
    if (parsed.departmentId) {
      this.onDepartmentChange(parsed.departmentId).then(() => {
        if (parsed.categoryId) {
          this.getSubCategories(parsed.categoryId).then(() => {
            this.patchSavedFormData(parsed);
          });
        } else {
          this.patchSavedFormData(parsed);
        }
      });
    } else {
      this.patchSavedFormData(parsed);
    }
  }

  // ADDED: Helper method to patch product data
  private patchProductData(res: any): void {
    this.productForm.patchValue(res);
    this.productForm.patchValue({ sku: res.sku });

    if (res.variants) {
      res.variants.forEach(() => this.addVariant());
      this.variants.patchValue(res.variants);

      res.variants.forEach((variant: any, index: number) => {
        const pricingArray = this.getPricingControls(index);
        if (variant.pricing?.length) {
          // Active rows, most recently re-priced first (the backend's
          // PRICING_NEWEST_FIRST), so retired and old generations sit at
          // the bottom ready to go. "Last updated" is priceUpdatedAt: when
          // the row's price was last set. updatedAt also moves on retiring
          // (it is the POS sync stamp), so it would show the retire time.
          const lud = (p: any) =>
            new Date(p.priceUpdatedAt ?? p.updatedAt).getTime();
          variant.pricing.sort(
            (a: any, b: any) =>
              Number(b.isActive !== false) - Number(a.isActive !== false) ||
              lud(b) - lud(a),
          );
          variant.pricing.forEach((pricing: any) => {
            this.pricingMeta.set(pricing.id, {
              updatedAt: pricing.priceUpdatedAt ?? pricing.updatedAt,
              isActive: pricing.isActive,
            });
            this.addPricing(index);
            pricing.validFrom = this.toDateOnly(pricing.validFrom);
            pricing.validTo = this.toDateOnly(pricing.validTo);
          });
          pricingArray.patchValue(variant.pricing);
        } else {
          this.addPricing(index);
        }
      });
    }

    if (res.manufacturerId) {
      this.ensureManufacturer(res.manufacturer ?? { id: res.manufacturerId });
      this.refreshBrandOptions();
    }

    if (res.bulkParentProductId) {
      this.productService
        .getProductById(res.bulkParentProductId)
        .subscribe((res: any) => {
          this.parentProducts = [res];
          this.productForm.patchValue({ bulkParentProductId: res.id });
          this.cdr.detectChanges();
        });
    }

    Promise.resolve().then(() => {
      this.isFormInitializing = false;
    });
  }

  // ADDED: Helper method to patch saved form data
  private patchSavedFormData(parsed: any): void {
    // Older drafts stored '' for empty combos; ng-select treats '' as a
    // selected value (shows the clear × and hides the placeholder).
    ['vendorsId', 'manufacturerId', 'brandId', 'departmentId', 'categoryId',
      'subCategoryId', 'uom', 'purchaseGstRateId'].forEach((k) => {
        if (parsed[k] === '') parsed[k] = null;
      });
    if (parsed.variants?.length) {
      parsed.variants.forEach(() => this.addVariant());
    }
    this.productForm.patchValue(parsed);
    if (parsed.manufacturerId) this.refreshBrandOptions();
    Promise.resolve().then(() => {
      this.isFormInitializing = false;
    });
  }

  // ADDED: Separate method to setup form subscriptions
  private setupFormSubscriptions(): void {
    this.productForm.get('departmentId')?.valueChanges.subscribe((id) => {
      if (this.categories.length > 0 && this.subCategories.length > 0)
        console.log(`[DepartmentChange] Selected Department: ${id}`);
      if (!this.isFormInitializing) {
        console.log(
          '[AddProductComponent] Manual change detected. Auto-generating SKU.',
        );
        this.autoGenerateSkuIfPossible();
      }
      if (id && !this.isFormInitializing) {
        this.onDepartmentChange(id);
      } else if (!id) {
        this.categories = [];
        this.subCategories = [];
        this.productForm.patchValue({ categoryId: null, subCategoryId: null });
        this.productForm.patchValue({ sku: '' });
      }
    });

    this.productForm.get('categoryId')?.valueChanges.subscribe((id) => {
      if (this.departments.length > 0 && this.subCategories.length > 0)
        console.log(`[CategoryChange] Selected Category: ${id}`);
      if (!this.isFormInitializing) {
        console.log(
          '[AddProductComponent] Manual change detected. Auto-generating SKU.',
        );
        this.autoGenerateSkuIfPossible();
      }
      if (id && !this.isFormInitializing) {
        this.getSubCategories(id);
      } else if (!id) {
        this.subCategories = [];
        this.productForm.patchValue({ subCategoryId: null });
        this.productForm.patchValue({ sku: '' });
      }
    });

    this.productForm.get('subCategoryId')?.valueChanges.subscribe(() => {
      if (this.departments.length > 0 && this.categories.length > 0)
        console.log(`[SubCategoryChange] Selected SubCategory`);
      if (!this.isFormInitializing) {
        console.log(
          '[AddProductComponent] Manual change detected. Auto-generating SKU.',
        );
        this.autoGenerateSkuIfPossible();
      }
      const currentSku = this.productForm.get('sku')?.value;
      if (!this.isEdit && !this.isFormInitializing && !currentSku) {
        console.log(`[SubCategoryChange] Generating SKU`);
        if (!this.isFormInitializing) {
          console.log(
            '[AddProductComponent] Manual change detected. Auto-generating SKU.',
          );
          this.autoGenerateSkuIfPossible();
        }
      }

      // Only clear SKU if not in edit mode or during initialization
      if (!this.isEdit && !this.isFormInitializing) {
        this.productForm.patchValue({ sku: '' });
      }
    });

    this.productForm
      .get('bulkParentProductId')
      ?.valueChanges.subscribe((productId) => {
        if (productId && !this.isFormInitializing) {
          // Find the selected product from either array
          let selectedProduct = this.parentProducts.find(
            (product) => product.id === productId,
          );
          if (!selectedProduct) {
            selectedProduct = this.parentProductsByName.find(
              (product) => product.id === productId,
            );
          }

          if (selectedProduct) {
            // Update the searchProductName field with the selected product's ID
            this.productForm.patchValue(
              {
                searchProductName: selectedProduct.id,
              },
              { emitEvent: false },
            );

            // Ensure the selected product is available in both arrays for display
            if (
              !this.parentProductsByName.find(
                (p) => p.id === selectedProduct.id,
              )
            ) {
              this.parentProductsByName = [
                selectedProduct,
                ...this.parentProductsByName,
              ];
            }
            if (!this.parentProducts.find((p) => p.id === selectedProduct.id)) {
              this.parentProducts = [selectedProduct, ...this.parentProducts];
            }
            this.cdr.detectChanges();
          }
        } else if (!productId) {
          // Clear the search product name when no parent product is selected
          this.productForm.patchValue(
            { searchProductName: null },
            { emitEvent: false },
          );
        }
      });

    // NEW: Add subscription for searchProductName changes (Name selection)
    this.productForm
      .get('searchProductName')
      ?.valueChanges.subscribe((productId) => {
        if (productId && !this.isFormInitializing) {
          // Find the selected product from either array
          let selectedProduct = this.parentProductsByName.find(
            (product) => product.id === productId,
          );
          if (!selectedProduct) {
            selectedProduct = this.parentProducts.find(
              (product) => product.id === productId,
            );
          }

          if (selectedProduct) {
            // Update the bulkParentProductId field with the selected product's ID
            this.productForm.patchValue(
              {
                bulkParentProductId: selectedProduct.id,
              },
              { emitEvent: false },
            );

            // Ensure the selected product is available in both arrays for display
            if (!this.parentProducts.find((p) => p.id === selectedProduct.id)) {
              this.parentProducts = [selectedProduct, ...this.parentProducts];
            }
            if (
              !this.parentProductsByName.find(
                (p) => p.id === selectedProduct.id,
              )
            ) {
              this.parentProductsByName = [
                selectedProduct,
                ...this.parentProductsByName,
              ];
            }

            // Force change detection to update the UI
            this.cdr.detectChanges();
          }
        }
      });
  }

  // ADDED: Separate method to setup search subscriptions
  private setupSearchSubscriptions(): void {
    this.parentProductSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => (this.isLoadingParentProducts = true)),
        switchMap((term: string) => {
          if (!term) {
            this.isLoadingParentProducts = false;
            return of([]);
          }
          return this.productService.searchBySKUWithContext(term, 'BULK').pipe(
            catchError(() => {
              this.isLoadingParentProducts = false;
              return of([]);
            }),
            finalize(() => (this.isLoadingParentProducts = false)),
          );
        }),
      )
      .subscribe((products) => (this.parentProducts = products));

    this.parentProductNameSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => (this.isLoadingParentProductsByName = true)),
        switchMap((term: string) => {
          if (!term) {
            this.isLoadingParentProductsByName = false;
            return of([]);
          }
          // Assuming you have a method to search by product name
          return this.productService.searchByNameWithContext(term).pipe(
            catchError(() => {
              this.isLoadingParentProductsByName = false;
              return of([]);
            }),
            finalize(() => (this.isLoadingParentProductsByName = false)),
          );
        }),
      )
      .subscribe((products) => {
        this.parentProductsByName = products;
      });
  }

  ngOnDestroy(): void {
    if (this.navigationSubscription) this.navigationSubscription.unsubscribe();
  }

  onManufacturerSelect(_event: any) {
    // Picked by hand: a later brand change must not clear it.
    this.mfrFromBrand = false;
    if (!this.brandLinked) return;
    this.refreshBrandOptions();
    // Keep the brand only if it belongs to the newly picked manufacturer.
    const brandId = this.val('brandId');
    if (brandId && !this.brandOptions.some((b: any) => b.id === brandId)) {
      this.productForm.patchValue({ brandId: null });
    }
  }

  /**
   * Every brand, or (brandLinked on, manufacturer picked) only that
   * manufacturer's brands.
   */
  private refreshBrandOptions() {
    const mfrId = this.val('manufacturerId');
    this.brandOptions =
      this.brandLinked && mfrId
        ? this.allBrands.filter((b: any) => b.manufacturerId === mfrId)
        : this.allBrands;
  }

  private loadAllBrands() {
    this.manufacturerService.getAllManufacturersBrands().subscribe({
      next: (res: any) => {
        this.allBrands = Array.isArray(res) ? res : res?.data ?? [];
        this.refreshBrandOptions();
      },
      error: () => (this.allBrands = []),
    });
  }

  /**
   * The manufacturer follows the brand: a brand under a manufacturer sets it
   * (replacing an earlier one); a standalone brand, or clearing the brand,
   * empties it again — unless the manufacturer was picked by hand.
   */
  onBrandSelect(brand: any) {
    const mfr = brand?.manufacturer;
    if (mfr?.id) {
      this.ensureManufacturer(mfr);
      this.productForm.patchValue({ manufacturerId: mfr.id });
      this.mfrFromBrand = true;
    } else if (this.mfrFromBrand) {
      this.productForm.patchValue({ manufacturerId: null });
      this.mfrFromBrand = false;
    }
    this.refreshBrandOptions();
  }

  /** Keeps a selected manufacturer (e.g. an inactive one) in the list. */
  private ensureManufacturer(mfr: any) {
    if (!mfr?.id || this.manufacturers.some((m: any) => m.id === mfr.id)) return;
    if (mfr.name) {
      this.manufacturers = [...this.manufacturers, mfr];
      return;
    }
    this.manufacturerService.getManufacturerById(mfr.id).subscribe((m: any) => {
      if (m?.id && !this.manufacturers.some((x: any) => x.id === m.id)) {
        this.manufacturers = [...this.manufacturers, m];
      }
    });
  }

  // Brand search also matches the manufacturer name ("Yardley" → its brands).
  brandSearch = (term: string, item: any) => {
    const t = term.toLowerCase();
    return (
      (item.name || '').toLowerCase().includes(t) ||
      (item.manufacturer?.name || '').toLowerCase().includes(t)
    );
  };

  // The whole active list, so the combo shows every manufacturer on open.
  getAllManufacturers() {
    this.manufacturerService
      .getManufacturers({ page: 1, limit: 10000, isActive: true })
      .subscribe((res: any) => {
        const list = [...(res?.data ?? [])].sort((a: any, b: any) =>
          (a.name || '').localeCompare(b.name || ''),
        );
        // Keep a selected one that is not in the active list (edit mode).
        const extra = this.manufacturers.filter(
          (s: any) => !list.some((m: any) => m.id === s.id),
        );
        this.manufacturers = [...list, ...extra];
        this.manufacturersBackup = this.manufacturers;
      });
  }

  // MODIFIED: Changed to return Promise for proper sequencing
  getAllVendors(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.vendorService.getAllVendorsList().subscribe({
        next: (res: any) => {
          this.vendors = res;
          resolve();
        },
        error: (err) => {
          reject(err);
        },
      });
    });
  }

  // MODIFIED: Changed to return Promise for proper sequencing
  getAllDepartments(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.productService.getAllDepartments().subscribe({
        next: (res: any) => {
          this.departments = res.data;
          resolve();
        },
        error: (err) => {
          reject(err);
        },
      });
    });
  }

  // MODIFIED: Changed to return Promise and fixed subcategory clearing logic
  onDepartmentChange(departmentId: string): Promise<void> {
    return new Promise((resolve) => {
      this.productService
        .getCategoriesByDepartment(departmentId)
        .subscribe((res: any) => {
          this.categories = res.data;
          // FIXED: Only clear subcategories if not during form initialization
          // and if the current categoryId is not valid for the new department
          if (!this.isFormInitializing) {
            const currentCategoryId = this.productForm.get('categoryId')?.value;
            const isCategoryValidForDepartment = this.categories.some(
              (cat: any) => cat.id === currentCategoryId,
            );

            if (!isCategoryValidForDepartment) {
              this.subCategories = [];
              this.productForm.patchValue({
                categoryId: null,
                subCategoryId: null,
              });
            }
          }
          resolve();
        });
    });
  }

  // MODIFIED: Changed to return Promise for proper sequencing
  getSubCategories(categoryId: string): Promise<void> {
    return new Promise((resolve) => {
      this.productService.getSubCategories(categoryId).subscribe((res) => {
        this.subCategories = res.data;
        resolve();
      });
    });
  }

  // MODIFIED: Changed to return Promise for proper sequencing
  loadGstSlabs(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.productService.getGstSlabs().subscribe({
        next: (res) => {
          this.gstSlabs = res?.data;
          resolve();
        },
        error: (err) => {
          reject(err);
        },
      });
    });
  }

  get reviewJson() {
    const value = this.productForm.value;
    return {
      ...value,
      categoryName: this.categories.find((c: any) => c.id === value.categoryId)
        ?.name,
      subCategoryName: this.subCategories.find(
        (s: any) => s.id === value.subCategoryId,
      )?.name,
      vendorName: this.vendors.find((v: any) => v.id === value.vendorsId)?.name,
    };
  }

  get variants() {
    return this.productForm.get('variants') as FormArray;
  }

  addVariant() {
    const variantGroup = this.fb.group({
      id: [null],
      variantName: [''],
      color: [''],
      size: [''],
      weight: [''],
      isActive: [true],
      pricing: this.fb.array([]),
    });
    this.variants.push(variantGroup);
    const pricingArray = variantGroup.get('pricing') as FormArray;
    if (!this.isEdit && pricingArray.length === 0) {
      this.addPricing(this.variants.length - 1);
    }
  }

  // NOTE: EAN codes are not generated in-app. A real GS1 EAN has to be
  // allocated externally, and bulk break-up items reuse their SKU number as
  // the code — so the EAN is always typed in on the pricing row by hand.

  removeVariant(index: number) {
    this.variants.removeAt(index);
  }

  getPricingControls(variantIndex: number): FormArray {
    return this.variants.at(variantIndex).get('pricing') as FormArray;
  }

  addPricing(variantIndex: number) {
    this.getPricingControls(variantIndex).push(
      this.fb.group(
        {
          id: [null],
          pluCode: [''],
          // EAN printed on the packs sold at this price revision. Blank
          // inherits the variant's current EAN on the backend.
          eanCode: [''],
          mrp: [''],
          costPrice: [''],
          basePrice: [''],
          sellingPrice: [''],
          // B2B list price (tax-exclusive). Optional.
          wholesalePrice: [''],
          validFrom: [''],
          validTo: [''],
        },
        { validators: this.pricingValidator }, // ✅ apply here
      ),
    );
  }

  // ── Inline "+ Add …" in the department / category / sub-category combos ──
  // ng-select awaits the returned promise and selects the created row, so the
  // usual valueChanges chain (load children, regenerate SKU) runs unchanged.

  addDepartmentTag = (name: string) =>
    this.createInline('Department', name, (n) =>
      this.productService.addDepartment({ name: n }),
    ).then((dept) => {
      if (dept) this.departments = [...this.departments, dept];
      return dept;
    });

  addManufacturerTag = (name: string) =>
    this.createInline('Manufacturer', name, (n) =>
      this.manufacturerService.quickCreateManufacturer(n),
    ).then((mfr) => {
      if (mfr && !this.manufacturers.some((m: any) => m.id === mfr.id)) {
        this.manufacturers = [...this.manufacturers, mfr];
      }
      return mfr;
    });

  addBrandTag = (name: string) => {
    const manufacturerId = this.val('manufacturerId');
    if (this.brandLinked && !manufacturerId) {
      this.toastr.warning('Please select a manufacturer first');
      return Promise.resolve(null);
    }
    // Linked: created under the picked manufacturer. Otherwise standalone.
    return this.createInline('Brand', name, (n) =>
      this.manufacturerService.createBrand(
        n,
        this.brandLinked ? manufacturerId : null,
      ),
    ).then((brand) => {
      if (brand && !this.allBrands.some((b: any) => b.id === brand.id)) {
        this.allBrands = [...this.allBrands, brand];
        this.refreshBrandOptions();
      }
      return brand;
    });
  };

  addCategoryTag = (name: string) => {
    const departmentsId = this.productForm.value.departmentId;
    if (!departmentsId) {
      this.toastr.warning('Please select a department first');
      return Promise.resolve(null);
    }
    return this.createInline('Category', name, (n) =>
      this.productService.addCategory({ name: n, departmentsId } as any),
    ).then((cat) => {
      if (cat) this.categories = [...this.categories, cat];
      return cat;
    });
  };

  addSubCategoryTag = (name: string) => {
    const categoryId = this.productForm.value.categoryId;
    if (!categoryId) {
      this.toastr.warning('Please select a category first');
      return Promise.resolve(null);
    }
    return this.createInline('Sub category', name, (n) =>
      this.productService.addSubCategory({
        name: n,
        categoryId,
        isActive: true,
      } as any),
    ).then((sub) => {
      if (sub) this.subCategories = [...this.subCategories, sub];
      return sub;
    });
  };

  private async createInline(
    label: string,
    name: string,
    create: (name: string) => Observable<any>,
  ): Promise<any> {
    const clean = (name ?? '').trim();
    if (!clean) return null;
    try {
      const res: any = await firstValueFrom(create(clean));
      const row = res?.data?.id ? res.data : res;
      this.toastr.success(`${label} "${clean}" added`);
      return row;
    } catch {
      this.toastr.error(`Could not add ${label.toLowerCase()}`);
      return null;
    }
  }

  goToStep(step: number) {
    if (step <= this.currentStep) {
      this.currentStep = step;
      window.scrollTo({ top: 0 });
      return;
    }
    if (this.productForm.valid) {
      if (step === 2 && this.variants.length === 0) {
        const shortDescription =
          this.productForm.get('shortDescription')?.value || 'Default Variant';
        this.variants.push(
          this.fb.group({
            variantName: [shortDescription],
            color: [''],
            size: [''],
            weight: [''],
            isActive: [true],
            pricing: this.fb.array([]),
          }),
        );
      }

      this.currentStep = step;
      window.scrollTo({ top: 0 });
    } else {
      this.markFormGroupTouched(this.productForm);
      this.toastr.error('Please fill in all required fields to continue.');
    }
  }

  markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched({ onlySelf: true });
      if ((control as FormGroup).controls) {
        this.markFormGroupTouched(control as FormGroup);
      }
    });
  }

  cloneVariant(index: number) {
    const variantToClone = this.variants.at(index).value;

    // 1. Initialize the cloned form group (the EAN lives on the pricing rows
    //    and is deliberately NOT copied — a clone is a different item)
    const cloned = this.fb.group({
      variantName: [variantToClone.variantName],
      color: [variantToClone.color],
      size: [variantToClone.size],
      weight: [variantToClone.weight],
      isActive: [variantToClone.isActive],
      pricing: this.fb.array([]),
    });

    // Copy pricing if any
    if (variantToClone.pricing?.length) {
      const pricingArray = cloned.get('pricing') as FormArray;
      variantToClone.pricing.forEach((p: any) => {
        pricingArray.push(
          this.fb.group({
            id: [null], // Always null for cloned pricing (new record)
            pluCode: [p.pluCode ?? ''],
            eanCode: [''], // A clone is a different item — it must not reuse the EAN
            mrp: [p.mrp],
            costPrice: [p.costPrice],
            basePrice: [p.basePrice],
            sellingPrice: [p.sellingPrice],
            wholesalePrice: [p.wholesalePrice ?? ''],
            validFrom: [p.validFrom],
            validTo: [p.validTo],
          }),
        );
      });
    }

    // 2. Insert the cloned variant into the FormArray
    this.variants.insert(index + 1, cloned);
  }

  isVariantInvalid(index: number): boolean {
    const variant = this.variants.at(index) as FormGroup;
    return variant.invalid;
  }

  saveProduct() {
    if (this.productForm.invalid) {
      this.productForm.markAllAsTouched();

      // Check for pricing validation errors specifically to give more helpful messages
      const variantsArray = this.productForm.get('variants') as FormArray;
      for (let i = 0; i < variantsArray.length; i++) {
        const variantGroup = variantsArray.at(i) as FormGroup;
        const pricingArray = variantGroup.get('pricing') as FormArray;
        const variantName =
          variantGroup.get('variantName')?.value || `Variant ${i + 1}`;

        for (let j = 0; j < pricingArray.length; j++) {
          const pricingGroup = pricingArray.at(j) as FormGroup;
          if (pricingGroup.invalid && pricingGroup.errors) {
            const errors = pricingGroup.errors;
            if (errors['basePriceInvalid']) {
              this.toastr.error(
                `${variantName}: ${errors['basePriceInvalid']}`,
                'Pricing Error',
              );
              return;
            }
            if (errors['mrpInvalid']) {
              this.toastr.error(
                `${variantName}: ${errors['mrpInvalid']}`,
                'Pricing Error',
              );
              return;
            }
            if (errors['sellingPriceInvalid']) {
              this.toastr.error(
                `${variantName}: ${errors['sellingPriceInvalid']}`,
                'Pricing Error',
              );
              return;
            }
          }
        }
      }

      this.toastr.error('Please fix validation errors');
      return;
    }
    if (this.productForm.valid) {
      const payload = this.cleanEmptyValues(this.productForm.value);

      if (this.isEdit) {
        this.productService.updateProduct(this.productId, payload).subscribe({
          next: () => {
            this.toastr.success('Product updated');
            this.formSubmitted = true;
            this.goBack();
          },
          error: (err) =>
            this.toastr.error(err?.error?.message || 'Update failed'),
        });
      } else {
        this.productService.addProduct(payload).subscribe({
          next: () => {
            localStorage.removeItem('productFormData');
            this.toastr.success('Product created');
            this.formSubmitted = true;
            this.goBack();
          },
          error: (err) =>
            this.toastr.error(err?.error?.message || 'Create failed'),
        });
      }
    } else {
      this.productForm.markAllAsTouched();
    }
  }

  clearForm() {
    this.productForm.reset();
    this.variants.clear();
    this.productForm.patchValue({
      isActive: true,
      returnToVendor: true,
      isConsumable: false,
      requiresBatchExpiryTracking: false,
      flowThrough: false,
      serialNumberControlled: false,
      cessApplicable: false,
      isDSD: false,
      isKit: false,
    });

    localStorage.removeItem('productFormData');
    this.mfrFromBrand = false;
    this.refreshBrandOptions();
    this.isBulk = false;
    this.showBulkProductsDetails = false;
    this.currentStep = 1;
  }

  goBack() {
    this.router.navigate(['/product-list']);
  }

  toDateOnly(dateStr: string): string | null {
    return dateStr ? dateStr.split('T')[0] : null;
  }

  cleanEmptyValues(obj: any): any {
    if (Array.isArray(obj)) {
      return obj
        .map((item) => this.cleanEmptyValues(item))
        .filter((item) => item && Object.keys(item).length > 0);
    }

    if (typeof obj === 'object' && obj !== null) {
      const cleanedObj: any = {};
      Object.keys(obj).forEach((key) => {
        let value = obj[key];

        // Exclude unwanted properties from the final payload
        if (key === 'packSizeGms' || key === 'searchProductName') {
          return;
        }

        // Convert specific string values to numbers where needed
        if (key === 'packSizeGm' && typeof value === 'string') {
          value = parseInt(value, 10);
          if (isNaN(value)) {
            value = null;
          }
        }

        // Handle variant/pricing IDs in edit mode
        // If id is null, remove it from payload (indicates new record)
        if (key === 'id' && value === null) {
          return; // Don't include null IDs
        }

        // Recursively clean nested objects and arrays
        const cleanedValue = this.cleanEmptyValues(value);

        // Add the key-value pair only if the value is not empty
        if (
          cleanedValue !== '' &&
          cleanedValue !== null &&
          cleanedValue !== undefined &&
          !(
            typeof cleanedValue === 'object' &&
            Object.keys(cleanedValue).length === 0
          )
        ) {
          cleanedObj[key] = cleanedValue;
        }
      });
      return cleanedObj;
    }

    return obj;
  }

  removePricing(variantIndex: number, pricingIndex: number): void {
    const pricingArray = this.getPricingControls(variantIndex);
    const id = pricingArray.at(pricingIndex)?.value?.id;
    // An unsaved row only lives in the form.
    if (!id) {
      pricingArray.removeAt(pricingIndex);
      return;
    }
    const p = pricingArray.at(pricingIndex).value;
    const lud = this.pricingMeta.get(id)?.updatedAt;
    const ludText = lud
      ? new Date(lud).toLocaleString('en-IN', {
          day: '2-digit', month: 'short', year: 'numeric',
          hour: '2-digit', minute: '2-digit', hour12: true,
        })
      : '';
    this.confirmDialog
      .confirm({
        title: 'Delete this price?',
        message: `MRP ₹${p.mrp} · Selling ₹${p.sellingPrice}${ludText ? ' · last updated ' + ludText : ''}<br><br><b>It is deleted right away — no need to click Save.</b><br>If earlier bills or stock documents used this price, it is kept (inactive) instead so those records stay intact.`,
        confirmText: 'Delete price',
        cancelText: 'Keep',
        tone: 'danger',
        icon: 'bi-trash3',
      })
      .then((ok) => {
        if (!ok) return;
        this.productService.deleteVariantPricing(id).subscribe({
          next: (res: any) => {
            if (res?.retired) {
              this.pricingMeta.set(id, {
                ...this.pricingMeta.get(id),
                isActive: false,
              });
              this.toastr.info(res.message);
            } else {
              this.pricingMeta.delete(id);
              pricingArray.removeAt(pricingIndex);
              this.toastr.success('Price deleted');
            }
          },
          error: (err) =>
            this.toastr.error(err?.error?.message || 'Could not delete the price'),
        });
      });
  }

  pricingUpdatedAt(price: AbstractControl): string | undefined {
    const id = price.value?.id;
    return id ? this.pricingMeta.get(id)?.updatedAt : undefined;
  }

  /** The backend refuses to delete a variant's last active saved price. */
  isOnlyActivePrice(variantIndex: number, price: AbstractControl): boolean {
    if (!price.value?.id) return false;
    const activeSaved = this.getPricingControls(variantIndex).controls.filter(
      (c) => c.value?.id && this.pricingMeta.get(c.value.id)?.isActive !== false,
    );
    return activeSaved.length === 1;
  }

  costPriceValidator: ValidatorFn = (
    group: AbstractControl,
  ): ValidationErrors | null => {
    const mrp = group.get('mrp')?.value;
    const costPrice = group.get('costPrice')?.value;

    if (
      mrp !== null &&
      costPrice !== null &&
      mrp !== '' &&
      costPrice !== '' &&
      parseFloat(costPrice) > parseFloat(mrp)
    ) {
      return { costPriceExceedsMRP: true };
    }

    return null;
  };

  autoGenerateSkuIfPossible() {
    const departmentId = this.productForm.get('departmentId')?.value;
    const categoryId = this.productForm.get('categoryId')?.value;
    const subCategoryId = this.productForm.get('subCategoryId')?.value;

    if (departmentId && categoryId && subCategoryId) {
      this.productService
        .generateNextSKU({ departmentId, categoryId, subCategoryId })
        .subscribe({
          next: (res) => {
            this.productForm.get('sku')?.enable();
            this.productForm.patchValue({ sku: res.sku });
          },
          error: (err) => {
            this.toastr.error(err?.error?.message || 'Failed to generate SKU');
          },
        });
    }
  }

  onIsBulkChange(isBulkValue: boolean) {
    this.isBulk = isBulkValue;

    const bulkParentProductIdControl = this.productForm.get(
      'bulkParentProductId',
    );
    const packSizeGmControl = this.productForm.get('packSizeGm');

    console.log('isBulkValue', isBulkValue);

    // ✅ FIX: Use setTimeout to defer all updates until after current change detection
    setTimeout(() => {
      this.productForm.patchValue(
        { isBulk: isBulkValue },
        { emitEvent: false },
      );

      if (isBulkValue) {
        bulkParentProductIdControl?.clearValidators();
        packSizeGmControl?.clearValidators();

        this.productForm
          .get('searchProductName')
          ?.setValue('', { emitEvent: false });

        // Clear the parent product arrays
        this.parentProducts = [];
        this.parentProductsByName = [];
      }

      bulkParentProductIdControl?.updateValueAndValidity();
      packSizeGmControl?.updateValueAndValidity();

      // Force change detection after all updates
      this.cdr.detectChanges();
    }, 0);
  }
  onParentProductSelect(selectedProduct: any) {
    if (selectedProduct) {
      // Ensure the product is in the name search array
      if (!this.parentProductsByName.some((p) => p.id === selectedProduct.id)) {
        this.parentProductsByName = [
          selectedProduct,
          ...this.parentProductsByName,
        ];
      }

      // Update both form controls
      this.productForm.patchValue(
        {
          bulkParentProductId: selectedProduct.id,
          searchProductName: selectedProduct.id,
        },
        { emitEvent: false },
      );

      this.cdr.detectChanges();
    }
  }

  onParentProductNameSelect(selectedProduct: any) {
    if (selectedProduct) {
      // Ensure the product is in the SKU search array
      if (!this.parentProducts.some((p) => p.id === selectedProduct.id)) {
        this.parentProducts = [selectedProduct, ...this.parentProducts];
      }

      // Update both form controls
      this.productForm.patchValue(
        {
          bulkParentProductId: selectedProduct.id,
          searchProductName: selectedProduct.id,
        },
        { emitEvent: false },
      );

      this.cdr.detectChanges();
    }
  }

  async canDeactivate(): Promise<boolean> {
    // If the form has been successfully saved, allow deactivation
    if (this.formSubmitted) {
      return true; // ✅ allow navigation without popup
    }

    // ✅ Show confirmation for edit page - always, regardless of dirty state
    if (this.isEdit && !this.isFormInitializing) {
      return this.confirmDialog.confirm({
        title: 'Leave Edit Page?',
        message:
          'Are you sure you want to leave <strong>Edit Product</strong> page?',
        confirmText: 'Leave',
        cancelText: 'Stay',
        size: 'lg',
      });
    }

    // ✅ Show confirmation for add page - always, regardless of dirty state
    if (!this.isEdit && !this.isFormInitializing) {
      return this.confirmDialog.confirm({
        title: 'Leave Page?',
        message:
          'Are you sure you want to leave <strong>Add Product</strong> page?',
        confirmText: 'Leave',
        cancelText: 'Stay',
        size: 'lg',
      });
    }

    return true;
  }

  pricingValidator: ValidatorFn = (
    group: AbstractControl,
  ): ValidationErrors | null => {
    // Compare at 3 decimals so float noise in a derived CP can't fail an
    // equal-looking pair (e.g. 84.50000001 vs 84.5).
    const round3 = (v: number) => Math.round(v * 1000) / 1000;
    const basePrice = round3(parseFloat(group.get('basePrice')?.value));
    const costPrice = round3(parseFloat(group.get('costPrice')?.value));
    const sellingPrice = round3(parseFloat(group.get('sellingPrice')?.value));
    const mrp = round3(parseFloat(group.get('mrp')?.value));

    if (
      !isNaN(basePrice) &&
      !isNaN(costPrice) &&
      !isNaN(sellingPrice) &&
      !isNaN(mrp)
    ) {
      if (basePrice > costPrice) {
        return { basePriceInvalid: 'Base Price must be less than Cost Price' };
      }
      if (mrp < costPrice) {
        return { mrpInvalid: 'MRP should be greater than or equal to Cost Price' };
      }
      if (sellingPrice < costPrice) {
        return {
          sellingPriceInvalid:
            'Selling Price should also be greater than or equal to Cost Price',
        };
      }
      if (sellingPrice > mrp) {
        return {
          sellingPriceInvalid:
            'Selling Price can be same as MRP but not greater',
        };
      }
    }

    return null;
  };

  /**
   * Selling at cost is valid, so it is surfaced as a hint next to the field
   * rather than as a form error that would block the save.
   */
  isZeroMargin(group: AbstractControl): boolean {
    const cp = parseFloat(group.get('costPrice')?.value);
    const sp = parseFloat(group.get('sellingPrice')?.value);
    return !isNaN(cp) && !isNaN(sp) && cp > 0 && sp > 0 && cp === sp;
  }

  // Inside AddProductComponent class

  // Gets the decimal GST rate (e.g., 0.18) based on the selected slab ID
  private getCurrentGstRate(): number {
    const gstSlabId = this.productForm.get('purchaseGstRateId')?.value;
    if (!gstSlabId) {
      return 0;
    }
    // Assuming gstSlabs has objects with 'id' and 'value' (the decimal rate)
    const slab = this.gstSlabs.find((s: any) => s.id === gstSlabId);
    // Ensure the slab value is treated as a number
    return slab ? parseFloat(slab.value) / 100 : 0;
    // NOTE: If 'value' in gstSlabs is already a decimal (e.g., 0.18), remove the / 100.
    // We assume 'value' is a percentage (e.g., 18).
  }

  calculatePricing(
    variantIndex: number,
    pricingIndex: number,
    changedField: 'mrp' | 'cost' | 'base',
  ): void {
    if (this.isFormInitializing) {
      return;
    }

    const marginPercent = Number(this.productForm.get('marginPercentage')?.value) || 0;
    const gstRateFactor = this.getCurrentGstRate();
    const pricingGroup = this.getPricingControls(variantIndex).at(
      pricingIndex,
    ) as FormGroup;

    const mrpCtrl = pricingGroup.get('mrp');
    const costCtrl = pricingGroup.get('costPrice');
    const baseCtrl = pricingGroup.get('basePrice');

    let mrp = Number(mrpCtrl?.value) || 0;
    let costPrice = Number(costCtrl?.value) || 0;
    let basePrice = Number(baseCtrl?.value) || 0;

    const patchData: any = {};

    if (changedField === 'mrp') {
      // From MRP: Update CP and Base ONLY if margin is > 0
      if (marginPercent > 0) {
        costPrice = mrp * (1 - marginPercent / 100);
        patchData.costPrice = parseFloat(costPrice.toFixed(4));

        basePrice = costPrice / (1 + gstRateFactor);
        patchData.basePrice = parseFloat(basePrice.toFixed(4));
      } else {
        // If margin is 0/blank, don't update CP from MRP automatically
        // but still update Base from changed CP if they exist? 
        // No, changedField is mrp, so we just do nothing to CP/Base.
      }
    } else if (changedField === 'cost') {
      // From CP: Always update Base (Base is always CP / (1+GST))
      basePrice = costPrice / (1 + gstRateFactor);
      patchData.basePrice = parseFloat(basePrice.toFixed(4));

      // Update MRP ONLY if margin > 0 AND (untouched OR empty OR CP > MRP)
      if (marginPercent > 0 && (mrpCtrl?.pristine || !mrpCtrl?.value || costPrice > mrp)) {
        mrp = marginPercent >= 100 ? costPrice : costPrice / (1 - marginPercent / 100);
        patchData.mrp = parseFloat(mrp.toFixed(4));
      }
    } else if (changedField === 'base') {
      // From Base Price: Always update CP (CP is always Base * (1+GST))
      costPrice = basePrice * (1 + gstRateFactor);
      patchData.costPrice = parseFloat(costPrice.toFixed(4));

      // Update MRP ONLY if margin > 0 AND (untouched OR empty OR CP > MRP)
      if (marginPercent > 0 && (mrpCtrl?.pristine || !mrpCtrl?.value || costPrice > mrp)) {
        mrp = marginPercent >= 100 ? costPrice : costPrice / (1 - marginPercent / 100);
        patchData.mrp = parseFloat(mrp.toFixed(4));
      }
    }

    // Only patch the fields that were NOT changed by the user to avoid cursor jump
    pricingGroup.patchValue(patchData, { emitEvent: false });

    pricingGroup.updateValueAndValidity();
    this.cdr.detectChanges();
  }

  onShowBulkProductsDetailsChange(checked: boolean): void {
    this.showBulkProductsDetails = checked;
  }

  // ── Product image upload ────────────────────────────────────────────────
  // The file goes to POST /media/product-images; the returned URL is stored
  // in photoUrl, so the payload and every screen that shows photoUrl are
  // unchanged. Pasting a URL still works.

  imageUploading = false;
  imageDragOver = false;
  imageBroken = false;
  private readonly imageTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

  onImagePicked(input: HTMLInputElement): void {
    const file = input.files?.[0];
    input.value = ''; // let the same file be picked again after a remove
    if (file) this.uploadImage(file);
  }

  onImageDrop(event: DragEvent): void {
    event.preventDefault();
    this.imageDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) this.uploadImage(file);
  }

  removeImage(): void {
    this.productForm.patchValue({ photoUrl: '' });
    this.productForm.get('photoUrl')?.markAsDirty();
    this.imageBroken = false;
  }

  private uploadImage(file: File): void {
    if (!this.imageTypes.includes(file.type)) {
      this.toastr.warning('Choose a PNG, JPG, WEBP or GIF image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.toastr.warning('Image must be 5 MB or smaller');
      return;
    }
    this.imageUploading = true;
    this.productService
      .uploadProductImage(file)
      .pipe(finalize(() => (this.imageUploading = false)))
      .subscribe({
        next: (res) => {
          this.imageBroken = false;
          this.productForm.patchValue({ photoUrl: res.url });
          this.productForm.get('photoUrl')?.markAsDirty();
        },
        error: (err) =>
          this.toastr.error(err?.error?.message || 'Image upload failed'),
      });
  }

  // ── View helpers (header, side panel, review) ───────────────────────────

  readonly steps = [
    { n: 1, label: 'Basic info' },
    { n: 2, label: 'Variants' },
    { n: 3, label: 'Review & submit' },
  ];

  /** Toggle grid in "Item properties" — every entry is a boolean control. */
  readonly propertySwitches = [
    { key: 'isPerishable', label: 'Is perishable' },
    { key: 'isTemperatureControlled', label: 'Is temperature controlled' },
    { key: 'requiresBatchExpiryTracking', label: 'Batch controlled' },
    { key: 'serialNumberControlled', label: 'Serial number controlled' },
    { key: 'isActive', label: 'Is active' },
    { key: 'flowThrough', label: 'Flow through' },
    { key: 'returnToVendor', label: 'Return to vendor' },
    { key: 'isConsumable', label: 'Is consumable' },
    { key: 'isDSD', label: 'Is DSD' },
    { key: 'isKit', label: 'Is kit component' },
    { key: 'isWeighable', label: 'Sold by weight (loose)' },
  ];

  nextStep(): void {
    if (this.currentStep < 3) this.goToStep(this.currentStep + 1);
    else this.saveProduct();
  }

  prevStep(): void {
    if (this.currentStep > 1) this.goToStep(this.currentStep - 1);
  }

  /** Reads the three "mandatory on new products" switches and applies them. */
  private loadMasterRequirements(): void {
    this.configurationService.getSettings().subscribe({
      next: (res) => {
        const flag = (key: string, fallback: boolean) => {
          const v = res.settings.find((s) => s.key === key)?.value;
          return typeof v === 'boolean' ? v : fallback;
        };
        this.masterRequired = {
          vendor: flag(SETTING_KEYS.PRODUCT_VENDOR_REQUIRED, true),
          manufacturer: flag(SETTING_KEYS.PRODUCT_MANUFACTURER_REQUIRED, false),
          brand: flag(SETTING_KEYS.PRODUCT_BRAND_REQUIRED, false),
        };
        this.brandLinked = flag(SETTING_KEYS.PRODUCT_BRAND_LINKED_TO_MANUFACTURER, false);
        this.refreshBrandOptions();
        this.applyMasterRequirements();
      },
      // A config outage must never block data entry: keep the defaults.
      error: () => this.applyMasterRequirements(),
    });
  }

  private applyMasterRequirements(): void {
    const set = (name: string, required: boolean) => {
      const c = this.productForm.get(name);
      if (!c) return;
      c.setValidators(required ? [Validators.required] : []);
      c.updateValueAndValidity({ emitEvent: false });
    };
    set('vendorsId', this.masterRequired.vendor);
    set('manufacturerId', this.masterRequired.manufacturer);
    set('brandId', this.masterRequired.brand);
  }

  /** True once a required control has been touched and is still invalid. */
  bad(name: string): boolean {
    const c = this.productForm.get(name);
    return !!c && c.invalid && c.touched;
  }

  val(name: string): any {
    return this.productForm.get(name)?.value;
  }

  private nameOf(list: any[], id: any, key = 'name'): string {
    if (id === null || id === undefined || id === '') return '';
    return (list || []).find((x: any) => x.id == id)?.[key] ?? '';
  }

  get departmentName() { return this.nameOf(this.departments, this.val('departmentId')); }
  get categoryName() { return this.nameOf(this.categories, this.val('categoryId')); }
  get subCategoryName() { return this.nameOf(this.subCategories, this.val('subCategoryId')); }
  get vendorName() { return this.nameOf(this.vendors, this.val('vendorsId')); }
  get brandName() { return this.nameOf(this.allBrands, this.val('brandId')); }
  get manufacturerName() { return this.nameOf(this.manufacturers, this.val('manufacturerId')); }
  get gstSlabLabel() {
    return this.nameOf(this.gstSlabs, this.val('purchaseGstRateId'), 'displayLabel');
  }

  get categoryPath(): string {
    return (
      [this.departmentName, this.categoryName, this.subCategoryName]
        .filter(Boolean)
        .join(' › ') || 'No category selected yet'
    );
  }

  get shelfHint(): { text: string; warn: boolean } {
    if (this.val('shelfLifeDays')) {
      return {
        text: `GRN rejects stock with under ${this.val('minShelfLifeDays') || 0} days left`,
        warn: false,
      };
    }
    return this.val('isPerishable')
      ? { text: 'Perishable SKUs need a shelf life', warn: true }
      : { text: 'Not shelf-life controlled', warn: false };
  }

  get bulkNote(): { text: string; on: boolean } {
    if (this.isBulk) {
      return {
        text: 'Child SKUs can now select this product as their parent bulk SKU. Receiving is done on this SKU and consumed by break-down.',
        on: true,
      };
    }
    if (this.showBulkProductsDetails) {
      return this.val('bulkParentProductId')
        ? { text: 'Mapped to its bulk parent. Stock is deducted from the parent on break-down.', on: true }
        : { text: 'Pick the bulk parent SKU this pack is produced from — by SKU code or by product name.', on: false };
    }
    return {
      text: 'Leave off for a standalone SKU that is neither a bulk parent nor a break-down child.',
      on: false,
    };
  }

  private get allPricing(): any[] {
    return (this.variants.value || []).flatMap((v: any) => v.pricing || []);
  }

  private get pricingComplete(): boolean {
    const rows = this.allPricing;
    return (
      rows.length > 0 &&
      rows.every((p: any) =>
        ['basePrice', 'costPrice', 'sellingPrice', 'mrp'].every(
          (k) => p[k] !== '' && p[k] !== null && p[k] !== undefined,
        ),
      )
    );
  }

  get checks() {
    const ok = (n: string) => !!this.productForm.get(n)?.valid;
    return [
      { label: 'Description', tag: 'Required', ok: ok('shortDescription') && ok('longDescription') },
      { label: 'Department & category', tag: 'Required', ok: ok('departmentId') && ok('categoryId') && ok('sku') },
      { label: 'Vendor & brand', tag: 'For purchase', ok: ok('vendorsId') && ok('manufacturerId') && ok('brandId') },
      { label: 'HSN & GST slab', tag: 'For invoicing', ok: ok('hsnCode') && ok('purchaseGstRateId') },
      { label: 'UOM', tag: 'For picking', ok: ok('uom') },
      { label: 'Variant pricing', tag: 'Required', ok: this.pricingComplete && this.variants.valid },
    ];
  }

  get readyPct(): number {
    const c = this.checks;
    return Math.round((c.filter((x) => x.ok).length / c.length) * 100);
  }

  get previewBrand(): string {
    return [this.brandName, this.val('uom')].filter(Boolean).join(' · ') || 'Brand not set';
  }

  get previewPrice(): string {
    const sp = parseFloat(this.allPricing[0]?.sellingPrice);
    return isNaN(sp) || !sp ? '₹—' : '₹' + this.f2(sp);
  }

  get sideHint(): string {
    if (this.currentStep === 1)
      return 'Batch or serial controlled SKUs cannot be switched off later once stock exists against them.';
    if (this.currentStep === 2)
      return 'Base, cost, selling price and MRP are mandatory on every pricing row. Margin % (Tax info) drives the auto-fill.';
    return this.isEdit
      ? 'Saving updates the SKU and all its variants.'
      : 'Submitting creates the SKU and all its variants. Barcodes print from the product page.';
  }

  get reviewTiles() {
    const v = (n: string) => this.val(n);
    const has = (x: any) => x !== '' && x !== null && x !== undefined;
    const controls = [
      v('requiresBatchExpiryTracking') ? 'Batch' : '',
      v('serialNumberControlled') ? 'Serial' : '',
      v('isTemperatureControlled') ? 'Temp' : '',
      v('isWeighable') ? 'Weighed' : '',
    ].filter(Boolean);
    const flags = [
      v('flowThrough') ? 'Flow through' : '',
      v('returnToVendor') ? 'RTV' : '',
      v('isConsumable') ? 'Consumable' : '',
      v('isDSD') ? 'DSD' : '',
      v('isKit') ? 'Kit' : '',
    ].filter(Boolean);
    return [
      { label: 'Long description', value: v('longDescription') || '—' },
      { label: 'Short description', value: v('shortDescription') || '—' },
      { label: 'SKU code', value: v('sku') || '—', mono: true },
      { label: 'Department', value: this.departmentName || '—' },
      { label: 'Category', value: [this.categoryName, this.subCategoryName].filter(Boolean).join(' › ') || '—' },
      { label: 'Vendor', value: this.vendorName || 'Not mapped' },
      { label: 'Manufacturer · brand', value: [this.manufacturerName, this.brandName].filter(Boolean).join(' · ') || '—' },
      { label: 'HSN / GST', value: (v('hsnCode') || '—') + ' · ' + (this.gstSlabLabel || '—'), mono: true },
      { label: 'Margin', value: (v('marginPercentage') || 0) + '%', mono: true },
      { label: 'UOM & packing', value: [v('uom'), v('innerPackaging'), v('outerPackaging')].filter(has).join(' · ') || '—' },
      { label: 'Shelf life', value: v('shelfLifeDays') ? `${v('shelfLifeDays')} days · min ${v('minShelfLifeDays') || 0}` : 'Not controlled' },
      { label: 'Controls', value: controls.join(' · ') || 'None' },
      { label: 'Status', value: v('isActive') ? 'Active on save' : 'Created inactive' },
      { label: 'Payment terms', value: (v('productSpecificPaymentTerms') || 0) + ' days', mono: true },
      { label: 'Bulk mapping', value: this.isBulk ? 'Bulk parent SKU' : (v('bulkParentProductId') ? 'Mapped to a parent' : 'Standalone') },
      { label: 'Cess', value: v('cessApplicable') ? (v('cessRate') || 0) + '%' : 'Not applicable' },
      { label: 'Flags', value: flags.join(' · ') || 'None' },
    ];
  }

  get reviewRows() {
    return (this.variants.value || []).flatMap((v: any, i: number) =>
      (v.pricing || []).map((p: any) => {
        const cost = parseFloat(p.costPrice) || 0;
        const sell = parseFloat(p.sellingPrice) || 0;
        const mrp = parseFloat(p.mrp) || 0;
        const wholesale = parseFloat(p.wholesalePrice) || 0;
        const m = cost > 0 && sell > 0 ? ((sell - cost) / cost) * 100 : null;
        return {
          name: v.variantName || 'Variant ' + (i + 1),
          spec: [v.color, v.size].filter(Boolean).join(' · '),
          active: v.isActive !== false,
          ean: p.eanCode || '—',
          cost: cost ? this.f2(cost) : '—',
          sell: sell ? this.f2(sell) : '—',
          mrp: mrp ? this.f2(mrp) : '—',
          // Blank wholesale means B2B sells at the selling price.
          wholesale: wholesale ? this.f2(wholesale) : null,
          margin: m,
        };
      }),
    );
  }

  get pricingRowCount(): number {
    return this.allPricing.length;
  }

  variantSummary(i: number): string {
    const v = this.variants.at(i).value;
    const n = (v.pricing || []).length;
    const spec = [v.color, v.size].filter(Boolean).join(' · ');
    return [spec, `${n} pricing row${n === 1 ? '' : 's'}`].filter(Boolean).join(' · ');
  }

  f2(n: number): string {
    return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}
